import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Tab,
  Tabs,
  Badge,
  Button,
  ProgressBar,
  Row,
  Col,
  Card,
  Spinner,
} from 'react-bootstrap';
import { FaExclamationTriangle, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import { listarSubstabelecimentos, buscarSubstabelecimentoOrigem, excluirSubstabelecimento, listarAnexosProcuracao, uploadAnexoProcuracao, downloadAnexoProcuracao, excluirAnexoProcuracao } from '~/Services/Service';
import { formatDocumento } from './helpers';

const calcVigencia = (proc) => {
  if (!proc.data_vencimento) return { percent: 0, variant: 'success', label: 'Indeterminada' };

  const inicio = moment(proc.data_lavratura);
  const fim = moment(proc.data_vencimento);
  const hoje = moment();
  const totalDias = fim.diff(inicio, 'days');
  const passados = hoje.diff(inicio, 'days');

  if (totalDias <= 0) return { percent: 100, variant: 'danger', label: 'Vencida' };

  const pct = Math.min(Math.round((passados / totalDias) * 100), 100);

  let variant = 'success';
  if (fim.diff(hoje, 'days') <= 90) variant = 'warning';
  if (fim.isBefore(hoje, 'day')) variant = 'danger';

  return { percent: pct, variant, label: pct + '%' };
};

const BADGE_INSTRUMENTO = {
  'Público': 'primary',
  'Privado': 'warning',
  'Certidão': 'success',
};

const BADGE_TIPO = {
  'Aprovação': 'primary',
  'Comercialização': 'success',
  'Pós-registro': 'warning',
  'Geral/amplo': 'dark',
  'Substabelecimento': 'danger',
  'Misto': 'info',
};

const TabGeral = ({ proc }) => {
  const vig = calcVigencia(proc);

  return (
    <div className="p-3">
      <Row className="mb-3">
        <Col md={6}>
          <small className="text-muted d-block">Instrumento</small>
          <Badge variant={BADGE_INSTRUMENTO[proc.descricao_tipo_instrumento] || 'secondary'}>
            {proc.descricao_tipo_instrumento}
          </Badge>
        </Col>
        <Col md={6}>
          <small className="text-muted d-block">Tipo Poder</small>
          <Badge variant={BADGE_TIPO[proc.descricao_tipo_poder] || 'secondary'}>
            {proc.descricao_tipo_poder}
          </Badge>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <small className="text-muted d-block">Data Lavratura</small>
          <strong>{moment(proc.data_lavratura).format('DD/MM/YYYY')}</strong>
        </Col>
        <Col md={4}>
          <small className="text-muted d-block">Vencimento</small>
          <strong>
            {proc.data_vencimento
              ? moment(proc.data_vencimento).format('DD/MM/YYYY')
              : 'Indeterminada'}
          </strong>
        </Col>
        <Col md={4}>
          <small className="text-muted d-block">Substabelecimento</small>
          <span>{proc.descricao_tipo_substabelecimento}</span>
        </Col>
      </Row>

      {proc.data_vencimento && (
        <div className="mb-3">
          <ProgressBar
            now={vig.percent}
            variant={vig.variant}
            label={vig.label}
            style={{ height: '20px' }}
          />
          <div className="d-flex justify-content-between mt-1">
            <small className="text-muted">
              {moment(proc.data_lavratura).format('DD/MM/YYYY')}
            </small>
            <small className="text-muted">
              {moment(proc.data_vencimento).format('DD/MM/YYYY')}
            </small>
          </div>
        </div>
      )}

      <Row className="mb-3">
        <Col md={6}>
          <small className="text-muted d-block">Tabelião</small>
          <span>{proc.tabeliao_nome || '-'}</span>
          {proc.tabeliao_detalhes && (
            <small className="d-block text-muted">{proc.tabeliao_detalhes}</small>
          )}
        </Col>
        <Col md={6}>
          <small className="text-muted d-block">Codigo e-Notariado</small>
          <span style={{ fontFamily: 'monospace' }}>
            {proc.codigo_enotoriado || '-'}
          </span>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <small className="text-muted d-block">Estados</small>
          <span>{proc.estados_validade || '-'}</span>
        </Col>
        <Col md={4}>
          <small className="text-muted d-block">Criado por</small>
          <span>{proc.nome_usuario_criador}</span>
        </Col>
        <Col md={4}>
          <small className="text-muted d-block">Data Criacao</small>
          <span>{moment(proc.data_criacao).format('DD/MM/YYYY HH:mm')}</span>
        </Col>
      </Row>

      {proc.observacao && (
        <div>
          <small className="text-muted d-block">Observação</small>
          <p className="mb-0">{proc.observacao}</p>
        </div>
      )}
    </div>
  );
};

const TabOutorgantes = ({ outorgantes }) => (
  <div className="p-3">
    <small className="text-muted d-block mb-2">
      Outorgantes ({(outorgantes || []).length})
    </small>
    <Row style={{ maxHeight: '400px', overflowY: 'auto' }} className="mx-0">
      {(outorgantes || []).map((o) => (
        <Col md={6} key={o.codigo} className="mb-2">
          <Card className="h-100">
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-start">
                <strong className="d-block">{o.nome}</strong>
                <small className="text-muted">{o.tipo_pessoa}</small>
              </div>
              <small style={{ fontFamily: 'monospace' }}>{formatDocumento(o.documento)}</small>
              {o.endereco && (
                <small className="d-block text-muted">{o.endereco}</small>
              )}
              {o.representante_legal && (
                <small className="d-block">Rep. Legal: {o.representante_legal}</small>
              )}
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  </div>
);

const TabOutorgados = ({ outorgados }) => (
  <div className="p-3">
    <small className="text-muted d-block mb-2">
      Procuradores nomeados ({(outorgados || []).length})
    </small>
    <Row style={{ maxHeight: '400px', overflowY: 'auto' }} className="mx-0">
      {(outorgados || []).map((o) => (
        <Col md={6} key={o.codigo} className="mb-2">
          <Card className="h-100">
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-start">
                <strong className="d-block">{o.nome}</strong>
              </div>
              <small style={{ fontFamily: 'monospace' }}>{formatDocumento(o.documento)}</small>
              {o.oab && <small className="d-block">OAB: {o.oab}</small>}
              {o.endereco && (
                <small className="d-block text-muted">{o.endereco}</small>
              )}
              {o.grupo && (
                <Badge variant="info" className="mt-1">
                  Grupo {o.grupo}
                </Badge>
              )}
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  </div>
);

const TabPoderes = ({ poderes }) => (
  <div className="p-3">
    <small className="text-muted d-block mb-2">
      Poderes conferidos — {(poderes || []).length} item(ns)
    </small>
    {(poderes || []).map((p) => {
      if (p.restricao) {
        return (
          <div
            key={p.codigo}
            className="mb-2 p-2 rounded"
            style={{
              backgroundColor: '#FDEAEA',
              color: '#7A1A1A',
              borderLeft: '3px solid #dc3545',
            }}
          >
            <strong>
              <FaExclamationTriangle className="mr-1" />
              {p.numero}
            </strong>{' '}
            — {p.descricao}
            {p.grupo && <Badge variant="info" className="ml-2">Grupo {p.grupo}</Badge>}
          </div>
        );
      }
      return (
        <div key={p.codigo} className="mb-2 p-2 border rounded">
          <strong>{p.numero}</strong> — {p.descricao}
          {p.grupo && <Badge variant="info" className="ml-2">Grupo {p.grupo}</Badge>}
        </div>
      );
    })}
  </div>
);

export default function ProcuracaoModal({ detalhe, loading, onClose, onNavegar, onEditar, onInativar, onCriarSubstabelecimento }) {
  const [abaAtiva, setAbaAtiva] = useState('geral');
  const [substabelecimentos, setSubstabelecimentos] = useState([]);
  const [substOrigem, setSubstOrigem] = useState(null);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [anexos, setAnexos] = useState([]);
  const [loadingAnexos, setLoadingAnexos] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const proc = detalhe ? detalhe.procuracao : null;
  const outorgantes = detalhe ? detalhe.outorgantes : [];
  const outorgados = detalhe ? detalhe.outorgados : [];
  const poderes = detalhe ? detalhe.poderes : [];

  const carregarSubs = useCallback(async (codigo) => {
    setLoadingSubs(true);
    try {
      const [subs, origem] = await Promise.all([
        listarSubstabelecimentos(codigo),
        buscarSubstabelecimentoOrigem(codigo),
      ]);
      setSubstabelecimentos(Array.isArray(subs) ? subs : []);
      setSubstOrigem(origem || null);
    } catch (e) {
      setSubstabelecimentos([]);
      setSubstOrigem(null);
    } finally {
      setLoadingSubs(false);
    }
  }, []);

  const carregarAnexos = useCallback(async (codigo) => {
    setLoadingAnexos(true);
    try {
      const data = await listarAnexosProcuracao(codigo);
      setAnexos(Array.isArray(data) ? data : []);
    } catch (e) {
      setAnexos([]);
    } finally {
      setLoadingAnexos(false);
    }
  }, []);

  useEffect(() => {
    if (proc) {
      setAbaAtiva('geral');
      carregarSubs(proc.codigo);
      carregarAnexos(proc.codigo);
    }
  }, [proc, carregarSubs, carregarAnexos]);

  const EXTENSOES_PERMITIDAS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.zip', '.rar'];

  const handleUpload = useCallback(async (e) => {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    const extensao = '.' + arquivo.name.split('.').pop().toLowerCase();
    if (EXTENSOES_PERMITIDAS.indexOf(extensao) < 0) {
      toast.error('Extensão não permitida:' + extensao);
      return;
    }
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    setUploading(true);
    try {
      await uploadAnexoProcuracao(proc.codigo, formData);
      toast.success('Anexo enviado com sucesso!');
      carregarAnexos(proc.codigo);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao enviar anexo';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [proc, carregarAnexos]);

  const handleDownload = useCallback(async (codigoAnexo, nomeArquivo) => {
    try {
      await downloadAnexoProcuracao(codigoAnexo, nomeArquivo);
    } catch (err) {
      toast.error('Erro ao baixar anexo');
    }
  }, []);

  const handleExcluirAnexo = useCallback(async (codigoAnexo) => {
    if (!window.confirm('Confirma exclusão do anexo?')) return;
    try {
      await excluirAnexoProcuracao(codigoAnexo);
      toast.success('Anexo excluído!');
      carregarAnexos(proc.codigo);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao excluir anexo';
      toast.error(msg);
    }
  }, [proc, carregarAnexos]);

  const handleExcluirSub = useCallback(async (codigoSub) => {
    if (!window.confirm('Confirma exclusão do substabelecimento?')) return;
    try {
      await excluirSubstabelecimento(codigoSub);
      toast.success('Substabelecimento excluído!');
      carregarSubs(proc.codigo);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao excluir substabelecimento';
      toast.error(msg);
    }
  }, [proc, carregarSubs]);

  if (!detalhe && !loading) return null;

  return (
    <>
    <Modal show centered size="lg" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="h6">
          {loading
            ? 'Carregando...'
            : (
              <>
                {'Procuração #' + proc.codigo + ' — ' + proc.descricao_tipo_instrumento}
              </>
            )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Tabs
            activeKey={abaAtiva}
            onSelect={(k) => { setAbaAtiva(k); }}
            className="px-3 pt-2"
          >
            <Tab eventKey="geral" title="Geral">
              <TabGeral proc={proc} />
            </Tab>
            <Tab eventKey="outorgantes" title={'Outorgantes (' + outorgantes.length + ')'}>
              <TabOutorgantes outorgantes={outorgantes} />
            </Tab>
            <Tab eventKey="outorgados" title={'Outorgados (' + outorgados.length + ')'}>
              <TabOutorgados outorgados={outorgados} />
            </Tab>
            <Tab eventKey="poderes" title={'Poderes (' + poderes.length + ')'}>
              <TabPoderes poderes={poderes} />
            </Tab>
            {(proc.codigo_tipo_substabelecimento !== 1 || substOrigem) && (
            <Tab eventKey="substabelecimentos" title={'Substab. (' + (substabelecimentos.length + (substOrigem ? 1 : 0)) + ')'}>
              <div className="p-3">
                {substOrigem && (
                  <Card className="mb-3 border-primary">
                    <Card.Body className="py-2">
                      <small className="text-muted d-block mb-1">Esta procuração é substabelecimento de:</small>
                      <Row>
                        <Col md={3}>
                          <strong
                            style={{ cursor: 'pointer', color: '#0747a6', textDecoration: 'underline' }}
                            onClick={() => { onNavegar(substOrigem.codigo_procuracao_origem); }}
                          >
                            Procuração #{substOrigem.codigo_procuracao_origem}
                          </strong>
                        </Col>
                        <Col md={2}>
                          <small className="text-muted d-block">Instrumento</small>
                          <span>{substOrigem.descricao_tipo_instrumento}</span>
                        </Col>
                        <Col md={2}>
                          <small className="text-muted d-block">Tipo Poder</small>
                          <span>{substOrigem.descricao_tipo_poder}</span>
                        </Col>
                        <Col md={3}>
                          <small className="text-muted d-block">Vencimento</small>
                          <span>{substOrigem.data_vencimento ? moment(substOrigem.data_vencimento).format('DD/MM/YYYY') : 'Indet.'}</span>
                        </Col>
                        <Col md={2}>
                          <small className="text-muted d-block">Reserva</small>
                          <Badge variant={substOrigem.com_reserva ? 'success' : 'secondary'}>
                            {substOrigem.com_reserva ? 'Com reserva' : 'Sem reserva'}
                          </Badge>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}
                {proc.codigo_tipo_substabelecimento !== 1 && onCriarSubstabelecimento && (
                  <div className="d-flex justify-content-center mb-2">
                    <Button variant="outline-primary" size="sm" onClick={() => { onCriarSubstabelecimento(proc.codigo); }}>
                      <FaPlus className="mr-1" /> Criar Substabelecimento
                    </Button>
                  </div>
                )}
                {loadingSubs ? (
                  <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                ) : substabelecimentos.length === 0 ? (
                  <div className="text-muted text-center">Nenhum substabelecimento registrado</div>
                ) : (
                  substabelecimentos.map((s) => (
                    <Card key={s.codigo} className="mb-2">
                      <Card.Body className="py-2">
                        <Row>
                          <Col md={3}>
                            <small className="text-muted d-block">Procuração Destino</small>
                            <strong>#{s.codigo_procuracao_destino}</strong>
                          </Col>
                          <Col md={2}>
                            <small className="text-muted d-block">Instrumento</small>
                            <span>{s.descricao_tipo_instrumento}</span>
                          </Col>
                          <Col md={2}>
                            <small className="text-muted d-block">Tipo Poder</small>
                            <span>{s.descricao_tipo_poder}</span>
                          </Col>
                          <Col md={2}>
                            <small className="text-muted d-block">Vencimento</small>
                            <span>{s.data_vencimento ? moment(s.data_vencimento).format('DD/MM/YYYY') : 'Indet.'}</span>
                          </Col>
                          <Col md={2}>
                            <small className="text-muted d-block">Reserva</small>
                            <Badge variant={s.com_reserva ? 'success' : 'secondary'}>
                              {s.com_reserva ? 'Com reserva' : 'Sem reserva'}
                            </Badge>
                          </Col>
                          <Col md={1} className="d-flex align-items-center justify-content-center">
                            {onInativar && (
                              <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => { handleExcluirSub(s.codigo); }}>
                                <FaTrash />
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))
                )}
              </div>
            </Tab>
            )}
            <Tab eventKey="anexos" title={'Anexos (' + anexos.length + ')'}>
              <div className="p-3">
                {onEditar && (
                  <div className="d-flex justify-content-center mb-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => { fileInputRef.current.click(); }}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <><Spinner as="span" animation="border" size="sm" className="mr-1" /> Enviando...</>
                      ) : (
                        <><FaPlus className="mr-1" /> Upload</>
                      )}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={EXTENSOES_PERMITIDAS.join(',')}
                      style={{ display: 'none' }}
                      onChange={handleUpload}
                    />
                  </div>
                )}
                {loadingAnexos ? (
                  <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                ) : anexos.length === 0 ? (
                  <div className="text-muted text-center">Nenhum anexo</div>
                ) : (
                  <table className="table table-sm table-bordered mb-0" style={{ fontSize: '13px' }}>
                    <thead style={{ backgroundColor: '#0747a6', color: '#fff' }}>
                      <tr>
                        <th>Arquivo</th>
                        <th>Enviado por</th>
                        <th>Data</th>
                        <th style={{ width: '80px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {anexos.map((a) => (
                        <tr key={a.codigo}>
                          <td>
                            <span
                              style={{ cursor: 'pointer', color: '#0747a6', textDecoration: 'underline' }}
                              onClick={() => { handleDownload(a.codigo, a.nome_arquivo); }}
                            >
                              {a.nome_arquivo}
                            </span>
                          </td>
                          <td>{a.nome_usuario || '-'}</td>
                          <td>{a.data ? moment(a.data).format('DD/MM/YYYY HH:mm') : '-'}</td>
                          <td className="text-center">
                            {onInativar && (
                              <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => { handleExcluirAnexo(a.codigo); }}>
                                <FaTrash />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Tab>
          </Tabs>
        )}
      </Modal.Body>
      {!loading && (onEditar || onInativar) && (
        <Modal.Footer>
          {onInativar && (
            <Button variant="outline-danger" size="sm" className="mr-auto" onClick={() => { onInativar(proc.codigo); }}>
              Excluir
            </Button>
          )}
          {onEditar && (
            <Button variant="outline-primary" size="sm" onClick={() => { onEditar(proc.codigo); }}>
              Editar
            </Button>
          )}
        </Modal.Footer>
      )}
    </Modal>

    </>
  );
}
